import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare } from "lucide-react";
import {
  getAssistantThread,
  listAssistantThreads,
  sendAssistantMessage,
  updateAssistantThreadStatus,
} from "@/api/assistant";
import { getTodayQueue } from "@/api/today";
import { AssistantRail } from "@/components/assistant/assistant-rail";
import { AssistantEmptyState } from "@/components/assistant/assistant-empty-state";
import { AssistantComposer } from "@/components/assistant/assistant-composer";
import { AssistantThreadView } from "@/components/assistant/assistant-thread";
import { CreditSettingsDialog } from "@/components/assistant/credit-settings-dialog";
import { MobileSheet } from "@/components/mobile-sheet";
import { readTodayPosition, todayHref } from "@/lib/today-position";
import type { AssistantMessage, CommandItem, StreamEvent } from "@ceo-cmd/shared";

function safeInternalPath(value?: string): string | undefined {
  return value?.startsWith("/") && !value.startsWith("//") ? value : undefined;
}

type TodayCommandContext = {
  commandItemId: string;
  companyId?: string;
  companyName?: string;
  portfolioId?: string;
  title?: string;
  recommendation?: string;
  situationSummary?: string;
  whyNow?: string;
  expectedOutcome?: string;
  riskOfInaction?: string;
  relevantContact?: string;
};

function contextFromItem(item: CommandItem): TodayCommandContext {
  return {
    commandItemId: item.id,
    companyId: item.companyId,
    companyName: item.companyName,
    portfolioId: item.portfolioId,
    title: item.title,
    recommendation: item.recommendation,
    situationSummary: item.situationSummary,
    whyNow: item.whyInToday,
    expectedOutcome: item.expectedOutcome,
    riskOfInaction: item.riskOfInaction,
    relevantContact: [item.contactName, item.contactTitle]
      .filter(Boolean)
      .join(" · "),
  };
}

export function AiAssistantPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const threadId = searchParams.get("thread");
  const resumeTaskHistory = searchParams.get("resumeTask") === "1";
  const commandItemId = searchParams.get("commandItemId") ?? undefined;
  const companyId = searchParams.get("companyId") ?? undefined;
  const companyName = searchParams.get("companyName") ?? undefined;
  const commandTitle = searchParams.get("title") ?? undefined;
  const commandRecommendation =
    searchParams.get("recommendation") ?? undefined;
  const portfolioId = searchParams.get("portfolioId") ?? undefined;
  const situationSummary = searchParams.get("situationSummary") ?? undefined;
  const whyNow = searchParams.get("whyNow") ?? undefined;
  const expectedOutcome = searchParams.get("expectedOutcome") ?? undefined;
  const riskOfInaction = searchParams.get("riskOfInaction") ?? undefined;
  const relevantContact = searchParams.get("relevantContact") ?? undefined;
  const returnTo = searchParams.get("returnTo") ?? undefined;
  const taskReturnTo =
    safeInternalPath(returnTo) ?? (commandItemId ? todayHref() : undefined);

  const [railCollapsed, setRailCollapsed] = useState(false);
  const [threadsSheetOpen, setThreadsSheetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [todayContext, setTodayContext] = useState<TodayCommandContext | null>(
    null,
  );
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [running, setRunning] = useState(false);
  const [streamState, setStreamState] = useState<{
    text: string;
    tools: Array<{
      name: string;
      label?: string;
      args?: Record<string, unknown>;
      href?: string;
    }>;
    verb?: string;
    steps?: string[];
    durationMs?: number;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // When opened from nav (no Ask AI deep-link), inherit Today selection / top Act Now.
  useEffect(() => {
    if (commandItemId) {
      setTodayContext(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const pos = readTodayPosition();
        const queue = await getTodayQueue({
          bucket: pos?.bucket ?? "act_now",
        });
        const selected = pos?.selectedId
          ? queue.items.find((i) => i.id === pos.selectedId)
          : undefined;
        const item = selected ?? queue.items[0];
        if (!cancelled && item) {
          setTodayContext(contextFromItem(item));
        } else if (!cancelled) {
          setTodayContext(null);
        }
      } catch {
        if (!cancelled) setTodayContext(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commandItemId]);

  const effective = {
    commandItemId: commandItemId ?? todayContext?.commandItemId,
    companyId: companyId ?? todayContext?.companyId,
    companyName: companyName ?? todayContext?.companyName,
    portfolioId: portfolioId ?? todayContext?.portfolioId,
    title: commandTitle ?? todayContext?.title,
    recommendation: commandRecommendation ?? todayContext?.recommendation,
    situationSummary: situationSummary ?? todayContext?.situationSummary,
    whyNow: whyNow ?? todayContext?.whyNow,
    expectedOutcome: expectedOutcome ?? todayContext?.expectedOutcome,
    riskOfInaction: riskOfInaction ?? todayContext?.riskOfInaction,
    relevantContact: relevantContact ?? todayContext?.relevantContact,
  };

  const threadsQuery = useQuery({
    queryKey: ["assistant-threads"],
    queryFn: listAssistantThreads,
  });

  useEffect(() => {
    if (
      !resumeTaskHistory ||
      !commandItemId ||
      threadId ||
      !threadsQuery.isSuccess
    ) {
      return;
    }
    const task = commandTitle ?? commandRecommendation;
    const legacyTaskTitle = (
      task && companyName ? `${companyName} · ${task}` : task ?? companyName
    )?.slice(0, 200);
    const priorTaskThread = threadsQuery.data.threads.find(
      (thread) =>
        thread.status !== "archived" &&
        (thread.commandItemId === commandItemId ||
          (!thread.commandItemId &&
            legacyTaskTitle &&
            thread.title === legacyTaskTitle)),
    );
    setSearchParams(
      {
        ...commandParams(),
        ...(priorTaskThread ? { thread: priorTaskThread.threadId } : {}),
      },
      { replace: true },
    );
  }, [
    commandItemId,
    commandRecommendation,
    commandTitle,
    companyName,
    resumeTaskHistory,
    setSearchParams,
    threadId,
    threadsQuery.data,
    threadsQuery.isSuccess,
  ]);

  const loadThread = useCallback(async (id: string) => {
    const res = await getAssistantThread(id);
    setMessages(res.messages);
    setStreamState(null);
  }, []);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setStreamState(null);
      return;
    }
    void loadThread(threadId).catch(() => {
      setSearchParams(commandParams(), { replace: true });
    });
  }, [threadId, loadThread, setSearchParams]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, streamState]);

  function commandParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (commandItemId) params.commandItemId = commandItemId;
    if (companyId) params.companyId = companyId;
    if (companyName) params.companyName = companyName;
    if (commandTitle) params.title = commandTitle;
    if (commandRecommendation) params.recommendation = commandRecommendation;
    if (portfolioId) params.portfolioId = portfolioId;
    if (situationSummary) params.situationSummary = situationSummary;
    if (whyNow) params.whyNow = whyNow;
    if (expectedOutcome) params.expectedOutcome = expectedOutcome;
    if (riskOfInaction) params.riskOfInaction = riskOfInaction;
    if (relevantContact) params.relevantContact = relevantContact;
    if (returnTo) params.returnTo = returnTo;
    return params;
  }

  function goNewChat() {
    abortRef.current?.abort();
    setSearchParams(commandParams(), { replace: true });
    setMessages([]);
    setStreamState(null);
  }

  function selectThread(id: string) {
    setSearchParams({ ...commandParams(), thread: id }, { replace: true });
  }

  async function setThreadArchived(id: string, archived: boolean) {
    await updateAssistantThreadStatus(id, archived ? "archived" : "active");
    if (archived && threadId === id) goNewChat();
    await queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
  }

  async function send(message: string, explicitThreadId?: string) {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setStreamState({ text: "", tools: [], verb: "Worked" });

    const activeThread = explicitThreadId ?? threadId ?? undefined;

    if (activeThread) {
      const optimistic: AssistantMessage = {
        PK: "",
        SK: "",
        EntityType: "ASSISTANT_MESSAGE",
        userId: "",
        threadId: activeThread,
        messageId: `local-${Date.now()}`,
        seq: (messages[messages.length - 1]?.seq ?? 0) + 1,
        role: "user",
        blocks: [{ type: "text", text: message }],
        feedback: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
    }

    try {
      const meta = await sendAssistantMessage(
        {
          message,
          threadId: activeThread,
          commandItemId: effective.commandItemId,
          companyId: effective.companyId,
          companyName: effective.companyName,
          portfolioId: effective.portfolioId,
          title: effective.title,
          recommendation: effective.recommendation,
          situationSummary: effective.situationSummary,
          whyNow: effective.whyNow,
          expectedOutcome: effective.expectedOutcome,
          riskOfInaction: effective.riskOfInaction,
          relevantContact: effective.relevantContact,
        },
        (event: StreamEvent, m) => {
          if (m.threadId && m.threadId !== threadId) {
            setSearchParams(
              { ...commandParams(), thread: m.threadId },
              { replace: true },
            );
          }
          if (event.type === "text") {
            setStreamState((prev) => {
              const raw = (prev?.text ?? "") + event.text;
              const text = raw
                .replace(/<<act:\w+\s*\{[\s\S]*?\}>>/g, "")
                .replace(/<<act:\w+\s*>>/g, "")
                .replace(/^Thinking:\s*.+$/gim, "")
                .trimStart();
              const steps = [
                ...(prev?.steps ?? []),
                ...[...raw.matchAll(/^Thinking:\s*(.+)$/gim)].map((mm) => mm[1]),
              ].filter((s, i, arr) => arr.indexOf(s) === i);
              return {
                text,
                tools: prev?.tools ?? [],
                verb: prev?.verb,
                steps: steps.length ? steps : prev?.steps,
                durationMs: prev?.durationMs,
              };
            });
          } else if (event.type === "tool") {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: [
                ...(prev?.tools ?? []),
                {
                  name: event.name,
                  label: event.label,
                  args: event.args,
                  href: event.href,
                },
              ],
              verb: prev?.verb,
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (event.type === "status" && event.label.endsWith("…")) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: event.label.replace(/…$/, ""),
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (
            event.type === "status" &&
            event.label === "message_saved"
          ) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: m.verb ?? prev?.verb,
              steps: m.steps ?? prev?.steps,
              durationMs: m.durationMs ?? prev?.durationMs,
            }));
          }
        },
        ac.signal,
      );

      const finalId = meta.threadId ?? activeThread;
      if (finalId) {
        await loadThread(finalId);
        if (!threadId && meta.threadId) {
          setSearchParams(
            { ...commandParams(), thread: meta.threadId },
            { replace: true },
          );
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setRunning(false);
      setStreamState(null);
    }
  }

  async function onPreset(preset: {
    id: string;
    name: string;
    text: string;
    autorun?: boolean;
  }) {
    await send(preset.text);
  }

  const threads = threadsQuery.data?.threads ?? [];
  const showEmpty = !threadId && messages.length === 0 && !streamState;

  return (
    <div className="flex h-full min-h-0 overflow-hidden rounded-lg border border-border bg-background">
      <div className="hidden h-full md:block">
        <AssistantRail
          collapsed={railCollapsed}
          onToggleCollapse={() => setRailCollapsed((c) => !c)}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={goNewChat}
          onSelectThread={selectThread}
          onSetThreadArchived={(id, archived) =>
            void setThreadArchived(id, archived)
          }
          onOpenSettings={() => setSettingsOpen(true)}
        />
      </div>

      <MobileSheet
        open={threadsSheetOpen}
        onClose={() => setThreadsSheetOpen(false)}
        title="Threads"
        variant="full"
      >
        <AssistantRail
          embedded
          collapsed={false}
          onToggleCollapse={() => undefined}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={() => {
            goNewChat();
            setThreadsSheetOpen(false);
          }}
          onSelectThread={(id) => {
            selectThread(id);
            setThreadsSheetOpen(false);
          }}
          onSetThreadArchived={(id, archived) =>
            void setThreadArchived(id, archived)
          }
          onOpenSettings={() => {
            setThreadsSheetOpen(false);
            setSettingsOpen(true);
          }}
        />
      </MobileSheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {(effective.companyName || effective.recommendation) && (
          <div className="flex items-start justify-between gap-3 border-b border-border bg-brand-soft/40 px-4 py-2 text-xs text-foreground">
            <div className="min-w-0">
              <p>
                {commandItemId ? "Context" : "Today context"}:{" "}
                {effective.companyName ?? "Command item"}
                {effective.recommendation
                  ? ` — ${effective.recommendation}`
                  : ""}
              </p>
              {(effective.whyNow || effective.relevantContact) && (
                <p className="mt-0.5 text-muted-foreground">
                  {[effective.relevantContact, effective.whyNow]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
            {taskReturnTo && (
              <button
                type="button"
                onClick={() => navigate(taskReturnTo)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-brand/40 bg-brand px-2.5 py-1.5 font-semibold text-brand-foreground shadow-sm transition hover:opacity-90"
              >
                <ArrowLeft className="size-3.5" />
                Back to task
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden">
          <button
            type="button"
            onClick={() => setThreadsSheetOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground"
          >
            <MessageSquare className="size-3.5" />
            Threads
          </button>
          <button
            type="button"
            onClick={goNewChat}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
          >
            New chat
          </button>
        </div>
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {showEmpty ? (
            <AssistantEmptyState
              onSend={(m) => void send(m)}
              onPreset={(p) => void onPreset(p)}
              running={running}
            />
          ) : (
            <AssistantThreadView
              threadId={threadId ?? "new"}
              messages={messages}
              streaming={streamState}
              onMessagesChange={setMessages}
            />
          )}
        </div>

        {!showEmpty && (
          <div className="border-t border-border px-4 py-3">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-2">
              <AssistantComposer
                placeholder="Reply to Assistant"
                disabled={running}
                onSend={(m) => void send(m)}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                Chats may be shared with third parties, see{" "}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => navigate("/settings/ai")}
                >
                  AI Policy
                </button>
                . Avoid sharing sensitive info.
              </p>
              {threadId && (
                <p className="font-mono text-[10px] text-faint">ID {threadId}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <CreditSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}
